import { Link } from "react-router";
import { ArrowRight, Shirt, ShoppingBag, Sparkles, Package, Home, Heart, Gamepad2, Utensils } from "lucide-react";
import { useInViewOnce } from "../hooks/useInViewOnce";
import { cn } from "../components/ui/utils";
import { HEADLINE_GRADIENT } from "../components/GradientSectionHeading";
import { BrandLogosMarquee } from "../components/BrandLogosMarquee";

export function IndustriesPage() {
  const { ref: headlineRef, inView: headlineInView } = useInViewOnce({ threshold: 0.28, rootMargin: "0px 0px -10% 0px" });

  return (
    <div className="min-h-screen" style={{ fontFamily: "'Manrope', sans-serif" }}>
      {/* HERO SECTION */}
      <section className="relative flex min-h-[768px] flex-col items-center justify-center overflow-hidden bg-[#F6F2EB] border-b border-[#131312]/5 py-32">
        <div className="absolute inset-0 flex justify-center pointer-events-none select-none overflow-hidden">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="100%"
            height="100%"
            fill="none"
            viewBox="0 0 2021 1100"
            className="w-full h-full"
            preserveAspectRatio="xMidYMid slice"
          >
            <style>{`
              @keyframes industries-line-draw {
                from { stroke-dashoffset: 1; }
                to { stroke-dashoffset: 0; }
              }
              @keyframes industries-line-draw-2 {
                from { stroke-dashoffset: 1; }
                to { stroke-dashoffset: 0; }
              }
              @keyframes industries-line-draw-3 {
                from { stroke-dashoffset: 1; }
                to { stroke-dashoffset: 0; }
              }
              @keyframes ocean-wave-1 {
                0%, 100% { transform: translateY(0px); }
                50% { transform: translateY(-18px); }
              }
              @keyframes ocean-wave-2 {
                0%, 100% { transform: translateY(0px); }
                50% { transform: translateY(14px); }
              }
              @keyframes ocean-wave-3 {
                0%, 100% { transform: translateY(0px); }
                50% { transform: translateY(-22px); }
              }
              @keyframes ocean-wave-4 {
                0%, 100% { transform: translateY(0px); }
                50% { transform: translateY(10px); }
              }
            `}</style>
            <g transform="translate(0, 0)">
              <g style={{ animation: "ocean-wave-1 6s ease-in-out 1.7s infinite" }}>
                <path stroke="url(#industries-grad-1)" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none" pathLength="1" strokeDasharray="1" strokeDashoffset="1" opacity="0.65" d="M-100 800 C200 400 500 900 800 500 C1100 100 1400 700 1700 350 C1900 150 2000 300 2121 250" style={{ animation: "industries-line-draw 1.6s ease-in-out 0.1s 1 normal forwards" }} />
              </g>
              <g style={{ animation: "ocean-wave-2 7s ease-in-out 2.1s infinite" }}>
                <path stroke="url(#industries-grad-2)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none" pathLength="1" strokeDasharray="1" strokeDashoffset="1" opacity="0.45" d="M-100 900 C250 500 550 950 850 580 C1150 200 1450 750 1750 420 C1950 220 2050 370 2121 330" style={{ animation: "industries-line-draw-2 1.9s ease-in-out 0.2s 1 normal forwards" }} />
              </g>
              <g style={{ animation: "ocean-wave-3 8s ease-in-out 2.5s infinite" }}>
                <path stroke="url(#industries-grad-3)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" pathLength="1" strokeDasharray="1" strokeDashoffset="1" opacity="0.3" d="M-100 700 C150 350 450 820 750 450 C1050 80 1350 650 1650 300 C1850 100 1950 220 2121 180" style={{ animation: "industries-line-draw 2.2s ease-in-out 0.3s 1 normal forwards" }} />
              </g>
              <g style={{ animation: "ocean-wave-4 9s ease-in-out 2.9s infinite" }}>
                <path stroke="url(#industries-grad-1)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" pathLength="1" strokeDasharray="1" strokeDashoffset="1" opacity="0.22" d="M-100 1000 C200 600 500 1050 800 650 C1100 250 1400 850 1700 500 C1900 300 2020 450 2121 430" style={{ animation: "industries-line-draw-2 2.5s ease-in-out 0.4s 1 normal forwards" }} />
              </g>
              <g style={{ animation: "ocean-wave-1 7.5s ease-in-out 3.3s infinite" }}>
                <path stroke="url(#industries-grad-2)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" pathLength="1" strokeDasharray="1" strokeDashoffset="1" opacity="0.18" d="M-100 600 C200 200 500 750 800 380 C1100 10 1400 600 1700 250 C1900 50 2000 180 2121 150" style={{ animation: "industries-line-draw-3 2.8s ease-in-out 0.5s 1 normal forwards" }} />
              </g>
              <g style={{ animation: "ocean-wave-2 8.5s ease-in-out 3.7s infinite" }}>
                <path stroke="url(#industries-grad-3)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" pathLength="1" strokeDasharray="1" strokeDashoffset="1" opacity="0.13" d="M-100 1050 C250 650 550 1000 850 680 C1150 300 1450 800 1750 480 C1950 280 2050 420 2121 480" style={{ animation: "industries-line-draw 3.1s ease-in-out 0.6s 1 normal forwards" }} />
              </g>
              <g style={{ animation: "ocean-wave-3 10s ease-in-out 4.1s infinite" }}>
                <path stroke="url(#industries-grad-1)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="none" pathLength="1" strokeDasharray="1" strokeDashoffset="1" opacity="0.09" d="M-100 550 C150 150 450 700 750 320 C1050 -50 1350 550 1650 200 C1850 0 1950 130 2121 100" style={{ animation: "industries-line-draw-2 3.4s ease-in-out 0.7s 1 normal forwards" }} />
              </g>
            </g>
            <defs>
              <linearGradient id="industries-grad-1" x1="0" y1="0" x2="2021" y2="0" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="#53A4CF" />
                <stop offset="40%" stopColor="#DA5B9B" />
                <stop offset="70%" stopColor="#F3D573" />
                <stop offset="100%" stopColor="#4E93FF" />
              </linearGradient>
              <linearGradient id="industries-grad-2" x1="0" y1="0" x2="2021" y2="0" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="#DA5B9B" />
                <stop offset="50%" stopColor="#F3D573" />
                <stop offset="100%" stopColor="#53A4CF" />
              </linearGradient>
              <linearGradient id="industries-grad-3" x1="0" y1="0" x2="2021" y2="0" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="#4E93FF" />
                <stop offset="100%" stopColor="#DA5B9B" />
              </linearGradient>
            </defs>
          </svg>
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-2 bg-[#FBF7F4] text-[#131312] border border-[#131312]/10 rounded-full text-sm font-medium mb-8 shadow-[0_4px_12px_-4px_rgba(0,0,0,0.15)]">
              Industry Expertise
            </div>
            <h1 ref={headlineRef} className="text-[48px] sm:text-[60px] md:text-[70px] lg:text-[78.1px] font-bold mb-8 leading-tight text-[#1C1C1C]">
              <span
                className={cn("gradient-text-animate inline", headlineInView && "gradient-text-animate--play")}
                style={{ backgroundImage: HEADLINE_GRADIENT }}
              >
                Industries We Serve
              </span>
            </h1>
            <p
              className="font-bold leading-[25px] text-[#1C1C1C] mx-auto"
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: '18.6px',
                maxWidth: '627px',
                letterSpacing: '-0.5px'
              }}
            >
              Specialized packaging solutions tailored to your industry's unique requirements and brand identity.
            </p>
          </div>
        </div>
      </section>

      {/* TRANSITION SPACER */}
      <section className="h-[170px] bg-[#FBFAF7] flex items-center overflow-hidden border-b border-[#131312]/5">
        <div className="w-full max-w-7xl mx-auto px-6 lg:px-8">
          <BrandLogosMarquee />
        </div>
      </section>

      <section className="py-20 bg-[#F6F2EB] border-b border-[#131312]/5">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <Link to="/products/hang-tags" className="group bg-[#FBFAF7] rounded-3xl p-10 hover:shadow-2xl transition-all border border-[#131312]/5">
              <div className="flex items-center justify-between mb-6">
                <div className="icon-box size-16 group-hover:scale-110 transition-transform">
                  <Shirt className="size-8" />
                </div>
                <div className="inline-flex items-center space-x-2 bg-[#F6F2EB] text-[#131312] px-3 py-1 rounded-full text-xs font-medium border border-[#131312]/10">
                  <span>Our Specialty</span>
                </div>
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-[#131312]">Textile & Fashion</h3>
              <p className="text-muted-foreground mb-6">
                As specialists in textile packaging, we understand the unique needs of fashion and apparel brands.
              </p>
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-[#F6F2EB] rounded-xl p-3 border border-[#131312]/5">
                  <div className="text-sm font-medium text-[#131312]">Hang Tags</div>
                </div>
                <div className="bg-[#F6F2EB] rounded-xl p-3 border border-[#131312]/5">
                  <div className="text-sm font-medium text-[#131312]">Insert Cards</div>
                </div>
                <div className="bg-[#F6F2EB] rounded-xl p-3 border border-[#131312]/5">
                  <div className="text-sm font-medium text-[#131312]">Garment Boxes</div>
                </div>
                <div className="bg-[#F6F2EB] rounded-xl p-3 border border-[#131312]/5">
                  <div className="text-sm font-medium text-[#131312]">Eco Options</div>
                </div>
              </div>
            </Link>

            <Link to="/products/folding-carton" className="group bg-[#FBFAF7] rounded-3xl p-10 hover:shadow-2xl transition-all border border-[#131312]/5">
              <div className="flex items-center justify-between mb-6">
                <div className="icon-box size-16 group-hover:scale-110 transition-transform">
                  <Sparkles className="size-8" />
                </div>
                <div className="inline-flex items-center space-x-2 bg-[#F6F2EB] text-[#131312] px-3 py-1 rounded-full text-xs font-medium border border-[#131312]/10">
                  <span>Premium</span>
                </div>
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-[#131312]">Cosmetics & Beauty</h3>
              <p className="text-muted-foreground mb-6">
                Luxury packaging that matches the quality of your beauty products and elevates your brand.
              </p>
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-[#F6F2EB] rounded-xl p-3 border border-[#131312]/5">
                  <div className="text-sm font-medium text-[#131312]">Luxury Cartons</div>
                </div>
                <div className="bg-[#F6F2EB] rounded-xl p-3 border border-[#131312]/5">
                  <div className="text-sm font-medium text-[#131312]">Perfume Boxes</div>
                </div>
                <div className="bg-[#F6F2EB] rounded-xl p-3 border border-[#131312]/5">
                  <div className="text-sm font-medium text-[#131312]">Folding Cartons</div>
                </div>
                <div className="bg-[#F6F2EB] rounded-xl p-3 border border-[#131312]/5">
                  <div className="text-sm font-medium text-[#131312]">Premium Finishes</div>
                </div>
              </div>
            </Link>

            <Link to="/products/corrugated-boxes" className="group bg-[#FBFAF7] rounded-3xl p-10 hover:shadow-2xl transition-all border border-[#131312]/5">
              <div className="flex items-center justify-between mb-6">
                <div className="icon-box size-16 group-hover:scale-110 transition-transform">
                  <ShoppingBag className="size-8" />
                </div>
                <div className="inline-flex items-center space-x-2 bg-[#F6F2EB] text-[#131312] px-3 py-1 rounded-full text-xs font-medium border border-[#131312]/10">
                  <span>E-Commerce</span>
                </div>
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-[#131312]">E-Commerce & Retail</h3>
              <p className="text-muted-foreground mb-6">
                Packaging designed for the digital age with memorable unboxing experiences.
              </p>
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-[#F6F2EB] rounded-xl p-3 border border-[#131312]/5">
                  <div className="text-sm font-medium text-[#131312]">Mailer Boxes</div>
                </div>
                <div className="bg-[#F6F2EB] rounded-xl p-3 border border-[#131312]/5">
                  <div className="text-sm font-medium text-[#131312]">Folding Cartons</div>
                </div>
                <div className="bg-[#F6F2EB] rounded-xl p-3 border border-[#131312]/5">
                  <div className="text-sm font-medium text-[#131312]">Branded Inserts</div>
                </div>
                <div className="bg-[#F6F2EB] rounded-xl p-3 border border-[#131312]/5">
                  <div className="text-sm font-medium text-[#131312]">Sustainable</div>
                </div>
              </div>
            </Link>

            <Link to="/products/pdq-displays" className="group bg-[#FBFAF7] rounded-3xl p-10 hover:shadow-2xl transition-all border border-[#131312]/5">
              <div className="flex items-center justify-between mb-6">
                <div className="icon-box size-16 group-hover:scale-110 transition-transform">
                  <Package className="size-8" />
                </div>
                <div className="inline-flex items-center space-x-2 bg-[#F6F2EB] text-[#131312] px-3 py-1 rounded-full text-xs font-medium border border-[#131312]/10">
                  <span>Versatile</span>
                </div>
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-[#131312]">Retail & Consumer Goods</h3>
              <p className="text-muted-foreground mb-6">
                Shelf-ready packaging that captures attention and drives sales in retail environments.
              </p>
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-[#F6F2EB] rounded-xl p-3 border border-[#131312]/5">
                  <div className="text-sm font-medium text-[#131312]">POS Displays</div>
                </div>
                <div className="bg-[#F6F2EB] rounded-xl p-3 border border-[#131312]/5">
                  <div className="text-sm font-medium text-[#131312]">Product Boxes</div>
                </div>
                <div className="bg-[#F6F2EB] rounded-xl p-3 border border-[#131312]/5">
                  <div className="text-sm font-medium text-[#131312]">Labels</div>
                </div>
                <div className="bg-[#F6F2EB] rounded-xl p-3 border border-[#131312]/5">
                  <div className="text-sm font-medium text-[#131312]">Seasonal</div>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#FBFAF7]">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-6xl font-semibold leading-[1.1] tracking-tight mb-4 text-[#131312]">Other Industries We Support</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Our expertise extends across many sectors, each with unique packaging requirements
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: Utensils, label: "Food & Beverage" },
              { icon: Heart, label: "Health & Wellness" },
              { icon: Package, label: "Electronics" },
              { icon: Gamepad2, label: "Toys & Games" },
              { icon: Home, label: "Home & Garden" },
              { icon: Heart, label: "Pet Products" },
              { icon: Sparkles, label: "Gifts & Stationery" },
              { icon: Package, label: "Automotive" },
            ].map((industry, idx) => (
              <div key={idx} className="bg-[#F6F2EB] rounded-2xl p-6 hover:shadow-xl transition-all group border border-[#131312]/5">
                <div className="icon-box bg-[#FBFAF7] size-12 mb-4 group-hover:scale-110 transition-transform">
                  <industry.icon className="size-6 text-[#131312]" />
                </div>
                <div className="font-bold text-[#131312]">{industry.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="relative py-24 bg-[#131312] text-white overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `
              radial-gradient(72.35% 197.74% at 70% 50%, rgba(255, 255, 255, 0.07) 0%, rgba(0, 0, 0, 0) 50%),
              radial-gradient(72.35% 197.74% at 30% 50%, rgba(255, 255, 255, 0.07) 0%, rgba(0, 0, 0, 0) 50%)
            `
          }}
        />
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-6xl font-bold mb-6 text-white max-w-4xl mx-auto leading-[1.1]">
            Ready to Create Premium <br /> Packaging?
          </h2>
          <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
            Get an instant quote and see how we can elevate your brand with custom packaging solutions
          </p>
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to="/quote"
              className="inline-flex h-12 w-full items-center justify-center rounded-xl bg-white text-sm font-medium text-[#131312] transition-colors hover:bg-white/90 shadow-lg sm:h-14 sm:w-auto sm:px-8 sm:text-base"
            >
              Get Instant Quote
              <ArrowRight className="ml-2 size-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex h-12 w-full items-center justify-center rounded-xl border border-white/20 bg-white/10 px-8 text-sm font-medium text-white backdrop-blur-sm transition-colors hover:bg-white/20 sm:h-14 sm:w-auto sm:text-base"
            >
              Schedule Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
